<script setup lang="ts">
  import SettingsPage from '@/components/app/settings/SettingsPage.vue'
  import CheckboxField from '@/components/admin/form/CheckboxField.vue'
  import { useTranslation } from '@/composables/useTranslation'
  import { computed, onMounted, ref, watch } from 'vue'
  import { useUserStore } from '@/shared/stores/userStore'
  import {
    type AdvoluxSettings,
    type AdvoluxSettingsRequest,
    type CustomerUser,
    RoleEnum,
    SettingsService
  } from '@/types/generated'
  import * as Sentry from '@sentry/vue'
  import { toast } from '@/components/ui/toast'

  const isLoading = ref(false)
  const { fetchUser } = useUserStore()
  const user = computed((): CustomerUser => useUserStore().user as CustomerUser)

  // Advolux integration fields
  const isAdvoluxEnabled = ref(false)

  const { t } = useTranslation({
    en: {
      warning: 'Warning',
      onlyAdminsAllowedMsg: 'Only the administrator is allowed to edit this setting.',
      description: 'Configure Advolux integration settings for JUPUS.',
      enableAdvolux: 'Enable Advolux Integration',
      saving: 'Saving...',
      success: 'Success',
      saveSuccessMsg: 'Advolux settings successfully saved.',
      error: 'Error',
      saveErrorMsg: 'Failed to save Advolux settings.'
    },
    de: {
      warning: 'Achtung',
      onlyAdminsAllowedMsg: 'Nur der Administrator kann diese Einstellung bearbeiten.',
      description: 'Die Advolux Schnittstelle',
      enableAdvolux: 'Advolux Integration aktivieren',
      saving: 'Speichern...',
      success: 'Erfolg',
      saveSuccessMsg: 'Advolux Einstellungen erfolgreich gespeichert.',
      error: 'Fehler',
      saveErrorMsg: 'Fehler beim Speichern der Advolux Einstellungen.'
    }
  })

  const internalAdvoluxUpdate = async (data: AdvoluxSettingsRequest) => {
    if (!user.value) {
      throw Error('User is not defined. Cannot update Advolux settings.')
    }

    await SettingsService.settingsAdvoluxCreate({
      requestBody: data
    })
  }

  const updateAdvoluxSettings = async () => {
    try {
      isLoading.value = true

      // Create data object with only the fields that have values
      const data: AdvoluxSettingsRequest = {
        is_advolux_export_integration_enabled: isAdvoluxEnabled.value
      }

      await internalAdvoluxUpdate(data)
      toast({
        title: t('success'),
        description: t('saveSuccessMsg'),
        variant: 'default'
      })
    } catch (error) {
      Sentry.captureException(error)
      toast({
        title: t('error'),
        description: t('saveErrorMsg'),
        variant: 'destructive'
      })
    } finally {
      isLoading.value = false
    }
  }

  onMounted(async () => {
    if (!user.value) {
      await fetchUser()
    }

    try {
      const advoluxSettings: AdvoluxSettings =
        (await SettingsService.settingsAdvoluxList()) as AdvoluxSettings

      // Initialize form with existing values if available
      if (advoluxSettings) {
        isAdvoluxEnabled.value = advoluxSettings.is_advolux_export_integration_enabled || false
      }
    } catch (error) {
      console.error('Error loading Advolux settings:', error)
      Sentry.captureException(error)
    }
  })

  // Watch for changes to update the UI accordingly
  watch([isAdvoluxEnabled], () => {
    // Only update if we have a user and the component is mounted
    if (user.value) {
      updateAdvoluxSettings()
    }
  })
</script>

<template>
  <SettingsPage v-if="user">
    <div class="space-y-8">
      <div
        v-if="user.role !== RoleEnum.ADMIN"
        class="mb-6"
      >
        <div class="mb-4 w-full rounded-md bg-orange-100 p-4 text-orange-700">
          <p class="font-medium">{{ t('warning') }}</p>
          <p class="mt-2">{{ t('onlyAdminsAllowedMsg') }}</p>
        </div>
      </div>
      <div
        v-else
        class="mb-6"
      >
        <!-- Advolux Integration Settings -->
        <div class="max-w-xl space-y-4">
          <!-- Enable Advolux Integration -->
          <CheckboxField
            v-model="isAdvoluxEnabled"
            name="is_advolux_export_integration_enabled"
            :label="t('enableAdvolux')"
          />
        </div>
      </div>
    </div>
  </SettingsPage>
</template>

<style scoped>
  /* Add any scoped styles here if needed */
</style>
